'use client'
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export type Lang = 'en' | 'hi' | 'ur' | 'te' | 'kn' | 'ta' | 'ml' | 'gu' | 'ks' | 'as'
export const RTL_LANGS = new Set<Lang>(['ur', 'ks'])
export const VALID_LANGS = ['en','hi','ur','te','kn','ta','ml','gu','ks','as'] as const

export const LANG_LABELS: Record<Lang, string> = {
  en:'English', hi:'हिन्दी', ur:'اردو', te:'తెలుగు',
  kn:'ಕನ್ನಡ', ta:'தமிழ்', ml:'മലയാളം', gu:'ગુજરાતી',
  ks:'کٲشُر', as:'অসমীয়া',
}

interface LanguageContextType {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: string, fallback?: string) => string
  isTranslating: boolean
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en', setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  isTranslating: false,
})

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('en')

  useEffect(() => {
    // Google Translate (whole-page) was RETIRED on web 2026-10-24 — it set dir=rtl + <html lang> but
    // never actually translated the text, leaving returning users stuck in RTL-English. Clear any
    // stored language + the googtrans cookie and reset the document to en/ltr so nobody is stranded.
    try { localStorage.removeItem('masjids_lang') } catch {}
    try {
      document.documentElement.dir = 'ltr'
      document.documentElement.lang = 'en'
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + window.location.hostname
    } catch {}
  }, [])

  const setLang = useCallback((newLang: Lang) => {
    if (!(VALID_LANGS as readonly string[]).includes(newLang)) return
    setLangState(newLang)
    try { localStorage.setItem('masjids_lang', newLang) } catch {}
    document.documentElement.dir = RTL_LANGS.has(newLang) ? 'rtl' : 'ltr'
    document.documentElement.lang = newLang
    // Trigger Google Translate
    if (typeof window !== 'undefined' && (window as any).translatePage) {
      (window as any).translatePage(newLang)
    }
  }, [])

  const t = useCallback((key: string, fallback?: string): string => {
    return key // Google Translate handles full page translation
  }, [])

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isTranslating: false }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  try { return useContext(LanguageContext) } catch {
    return { lang: 'en' as Lang, setLang: () => {}, t: (k: string, f?: string) => f ?? k, isTranslating: false }
  }
}

export default LanguageContext
